import { useMemo } from "react";
import { DropListWithIconItem, MarketRate } from "@cede/types";
import { API_ENDPOINT, Precise, QueryKeys, getCurrentRate, getErrorMessage } from "@cede/utils";
import { useTradePath } from "../api/useTradePath";
import { isMultipleTransactionsPreparedOrder, useMultipleTransactions } from "./useMultipleTransactions";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { AlertTypes } from "@/store";
import { useAlert } from "@/store/alert/alert";
import { useOrderBookStore } from "@/store/orderBookStore";
import { useQuery } from "@tanstack/react-query";

type UseMultipleTransactionsFeesParams = {
  enabled: boolean;
  accountId: string;
  fromToken: string;
  /**
   * The amount of the fromToken is needed to compute the fees
   */
  fromTokenAmount: string;
  toToken: string;
  network?: DropListWithIconItem<{
    withdrawMin?: string;
    withdrawFee?: string;
  }>;
  isInternalTransfer?: boolean;
};

// We compute the fees in the from currency
// We do the opposite operation regarding the classic order since we are reverting the order side
// If the orderSide is "buy" we need to multiply the fees by ask price of the market
// If the orderSide is "sell" we need to divide the fees by bid price of the market
const reverseComputeAmount = (amount: string, price: string, orderSide: "buy" | "sell") => {
  return orderSide === "buy" ? Precise.stringMul(amount, price) : Precise.stringDiv(amount, price);
};

/**
 * Hook to compute the fees for multiple transactions
 * !!important: You need to load the multiple transactions before using this hook
 */
export const useMultipleTransactionsFees = ({
  enabled,
  accountId,
  fromToken,
  fromTokenAmount,
  toToken,
  network: _network,
  isInternalTransfer: _isInternalTransfer = false,
}: UseMultipleTransactionsFeesParams) => {
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();
  const orderBook = useOrderBookStore((state) => state.orderBook);
  const { tradePath, isLoading: tradePathIsLoading } = useTradePath({
    enabled,
    accountId,
    fromToken,
    toToken,
  });
  // Withdrawals are not supported, so no need for networks
  const appendAlert = useAlert((state) => state.append);

  const { transactions, isTxLoading } = useMultipleTransactions({
    accountId,
    fromTokenAmount,
    tradePath,
  });

  const tradePathKey = useMemo(() => {
    if (!tradePath?.length) return "no-path";
    return tradePath.map(({ pairSymbol, orderSide }) => `${pairSymbol}:${orderSide}`).join("|");
  }, [tradePath]);

  const transactionsKey = useMemo(() => {
    if (!transactions.length) return "no-tx";
    return transactions
      .map((tx) =>
        isMultipleTransactionsPreparedOrder(tx)
          ? `${tx.request.pairSymbol}:${tx.isExecuted ? "1" : "0"}`
          : `withdrawal:${tx.isExecuted ? "1" : "0"}`,
      )
      .join("|");
  }, [transactions]);

  /**
   *
   * @returns The fees in the from currency
   */
  const _computeFees = async () => {
    if (!tradePath) return "0";
    try {
      // No withdrawal fees since withdrawals are not supported
      let _fees = "0";

      // We need to reverse the trade path in order to start from the final currency
      // and convert through the iterations to the first one
      for (const tradePathData of [...tradePath].reverse()) {
        const { pairSymbol, orderSide, from, to } = tradePathData;

        const account = hubUser?.exchangeAccounts.find((acc) => acc.id === Number(accountId));
        if (!account) continue;

        let marketRate: MarketRate = { bid: "0", ask: "0" };

        // Check orderBookStore cache first
        if (orderBook && orderBook.pairSymbol === pairSymbol && orderBook.exchangeId === account.exchangeId) {
          marketRate = {
            bid: String(orderBook.bids[0]?.[0] || 0),
            ask: String(orderBook.asks[0]?.[0] || 0),
          };
        } else if (auth.identityToken) {
          // Fallback to API
          marketRate = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE, {
            queryParams: {
              pairSymbol,
              exchangeId: account.exchangeId,
            },
          });
        }

        const price = getCurrentRate(marketRate, orderSide);

        _fees = reverseComputeAmount(_fees, price, orderSide) || "0";

        const preparedOrder = transactions.find(
          (tx) => isMultipleTransactionsPreparedOrder(tx) && tx.request.pairSymbol === pairSymbol,
        );

        if (preparedOrder && isMultipleTransactionsPreparedOrder(preparedOrder)) {
          const feeAmount = String(preparedOrder.estimatedFee.amount);
          // We compute the fees in the from currency
          const newFees =
            preparedOrder.estimatedFee.tokenSymbol === from
              ? String(preparedOrder.estimatedFee.amount)
              : preparedOrder.estimatedFee.tokenSymbol === to
                ? reverseComputeAmount(feeAmount, price, orderSide)
                : "0";

          _fees = Precise.stringAdd(_fees, newFees || "0") || "0";
        }
      }
      return _fees;
    } catch (error) {
      appendAlert(getErrorMessage(error), AlertTypes.ERROR);
      return "0";
    }
  };

  const { data: multipleTransactionFeesInFromToken, isFetching } = useQuery({
    queryKey: [QueryKeys.MULTIPLE_TX_FEES, tradePathKey, accountId, fromTokenAmount, transactionsKey],
    queryFn: _computeFees,
    enabled: !!tradePath && !!accountId && !!fromTokenAmount && transactions.length > 0 && enabled && !isTxLoading,
    staleTime: 1_000,
  });

  return {
    multipleTransactionFeesInFromToken,
    isLoading: isFetching || isTxLoading || tradePathIsLoading,
  };
};
